// Photographs and documents that department users upload to Google Drive.
//
// THE PROBLEM THIS SOLVES
// -----------------------
// Colleges need to put new campus photographs and circulars on the website themselves,
// without waiting for anybody to rebuild it. They do that by dropping files into a shared
// Google Drive folder, which has one sub-folder per college. This file is how the website
// finds out what is in that folder.
//
// WHERE THE LIST COMES FROM
// -------------------------
// Two sources, tried in this order:
//
//   1. REACT_APP_BACKEND_URL — the FastAPI backend's /api/drive/<college>. Used the moment
//      that variable is set, because the backend can also accept uploads through the site's
//      own dashboard. It is not deployed yet, so today this is skipped.
//   2. REACT_APP_DRIVE_MEDIA_URL — a Google Apps Script web app that reads the Drive folder
//      and returns it as JSON (see drive-media.gs at the root of this repository). This
//      needs no server of our own and no Google key in the website's code, because the
//      script runs as the folder's owner. If it is not set, the visitor counter's script
//      URL is used instead, since both live in the same Apps Script project.
//
// If neither answers, every caller gets an empty list and falls back to the photographs
// built into the site. No page is ever left blank because Drive was slow.
//
// WHEN THE FETCH HAPPENS
// ----------------------
// On page load, not at build time — that is what makes a new upload appear without a
// redeploy. The answer is then held in memory for a minute so that clicking between college
// pages does not re-ask seven times over. Reloading the page always asks again.

import { apiUrl, hasApi } from "./api";
import { envNumber, envUrl } from "./env";
import { appsScriptJsonp, fetchJson } from "./jsonp";

export const COLLEGE_SLUGS = [
  "eluru",
  "machilipatnam",
  "nandyal",
  "paderu",
  "piduguralla",
  "rajamahendravaram",
  "vizianagaram",
];

const SCRIPT_URL =
  envUrl(process.env.REACT_APP_DRIVE_MEDIA_URL) ||
  envUrl(process.env.REACT_APP_VISITOR_COUNTER_URL);

const CACHE_MS = envNumber(process.env.REACT_APP_DRIVE_CACHE_MS, 60 * 1000);
const REQUEST_TIMEOUT_MS = 12000;
const TRANSPORT_KEY = "apmercl_media_transport"; // "fetch" or "jsonp", whichever last worked

export const EMPTY_MEDIA = Object.freeze({
  photos: [],
  documents: [],
  folderUrl: "",
  source: "none",
});

/** True when some source of Drive media is configured. Lets the UI hide empty sections. */
export const hasDriveMedia = () => hasApi() || SCRIPT_URL !== "";

const readStore = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null; // Safari private mode, or cookies blocked outright
  }
};

const writeStore = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* storage unavailable — everything still works, it is just slower on the first try */
  }
};

// ---------------------------------------------------------------------------------------
// Turning either source's reply into one shape the pages can rely on
// ---------------------------------------------------------------------------------------

// A name a camera produced ("DSC_0142") reads better with its underscores opened out into
// spaces; a name somebody typed already has spaces and is left as they wrote it, so that
// "Nadu-Nedu Block" does not come out as "Nadu Nedu Block".
const titleFrom = (name) => {
  const base = String(name || "").replace(/\.[^.]+$/, "").trim();
  return (base.includes(" ") ? base : base.replace(/[_-]+/g, " ").trim()) || String(name || "Untitled");
};

const thumbUrl = (id, width) =>
  `https://drive.google.com/thumbnail?id=${encodeURIComponent(id)}&sz=w${width}`;

function normalisePhoto(file) {
  const id = file.id || "";
  return {
    id,
    title: file.title || titleFrom(file.name),
    // Google's thumbnail service is the dependable way to show a Drive image on an outside
    // page; the googleusercontent address is sharper but fails more often, so it is only
    // ever used to rescue a broken one.
    url: file.url || (id ? thumbUrl(id, 1600) : ""),
    thumb: file.thumb || (id ? thumbUrl(id, 600) : ""),
    fallback: file.full || (id ? `https://lh3.googleusercontent.com/d/${id}=w2400` : ""),
    src: file.view || file.viewLink || (id ? `https://drive.google.com/file/d/${id}/view` : ""),
    path: file.path || "",
    live: true,
  };
}

function normaliseDocument(file) {
  const id = file.id || "";
  return {
    id,
    name: file.name || "",
    title: file.title || titleFrom(file.name),
    label: file.label || labelFor(file.mimeType),
    view: file.view || file.viewLink || (id ? `https://drive.google.com/file/d/${id}/view` : ""),
    download:
      file.download ||
      (id ? `https://drive.google.com/uc?export=download&id=${encodeURIComponent(id)}` : ""),
    size: Number(file.size) || 0,
    path: file.path || "",
    modified: file.modified || file.modifiedTime || "",
  };
}

const MIME_LABELS = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "application/vnd.ms-excel": "Excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "Excel",
  "application/vnd.ms-powerpoint": "PowerPoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PowerPoint",
  "text/plain": "Text",
  "text/csv": "CSV",
  "application/vnd.google-apps.document": "Google Doc",
  "application/vnd.google-apps.spreadsheet": "Google Sheet",
  "application/vnd.google-apps.presentation": "Google Slides",
};

const labelFor = (mime) => MIME_LABELS[mime] || "File";

/** Human file size for a download link: "2.4 MB". */
export function formatSize(bytes) {
  if (!bytes) return "";
  const units = ["B", "KB", "MB", "GB"];
  let n = bytes;
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

// ---------------------------------------------------------------------------------------
// Source 1 — the FastAPI backend
// ---------------------------------------------------------------------------------------

async function fromBackend(slug) {
  const url = apiUrl(`/api/drive/${slug}`);
  if (!url) throw new Error("no backend");

  const data = await fetchJson(url, {}, REQUEST_TIMEOUT_MS);
  const files = Array.isArray(data && data.files) ? data.files : [];
  // `isImage` is the backend's own flag; `kind` is the Apps Script's. Accepting both means
  // this keeps working whichever of the two is answering.
  const isPhoto = (f) => f.isImage === true || f.kind === "photo";

  return {
    photos: files.filter(isPhoto).map(normalisePhoto),
    documents: files.filter((f) => !isPhoto(f)).map(normaliseDocument),
    folderUrl: data.folderId
      ? `https://drive.google.com/drive/folders/${data.folderId}`
      : "",
    source: "backend",
  };
}

// ---------------------------------------------------------------------------------------
// Source 2 — the Google Apps Script web app
// ---------------------------------------------------------------------------------------

async function callScript(action) {
  const url = `${SCRIPT_URL}?action=${action}`;

  // A reply the browser refused to hand over still reached the script, so retrying over the
  // script-tag route would be a second round trip every time. Remembering which one worked
  // keeps that cost to the first page load only.
  if (readStore(TRANSPORT_KEY) === "jsonp") {
    return appsScriptJsonp(url, REQUEST_TIMEOUT_MS);
  }
  try {
    // Deliberately a plain GET with no extra headers: that makes it a request the browser
    // will send without first asking permission, which Apps Script answers badly.
    const data = await fetchJson(url, { method: "GET", redirect: "follow" }, REQUEST_TIMEOUT_MS);
    writeStore(TRANSPORT_KEY, "fetch");
    return data;
  } catch {
    const data = await appsScriptJsonp(url, REQUEST_TIMEOUT_MS);
    writeStore(TRANSPORT_KEY, "jsonp");
    return data;
  }
}

// One request serves every college, so the whole payload is fetched once and shared.
let inFlight = null;
let payload = null;
let payloadAt = 0;

function loadScriptPayload() {
  const fresh = payload && Date.now() - payloadAt < CACHE_MS;
  if (fresh) return Promise.resolve(payload);
  if (inFlight) return inFlight;

  inFlight = callScript("media")
    .then((data) => {
      if (!data || data.ok === false || !data.colleges) {
        throw new Error((data && data.error) || "unexpected reply");
      }
      payload = data;
      payloadAt = Date.now();
      return data;
    })
    .finally(() => {
      inFlight = null;
    });

  return inFlight;
}

function fromScriptPayload(data, slug) {
  const entry = (data.colleges && data.colleges[slug]) || null;
  if (!entry) return EMPTY_MEDIA;
  return {
    photos: (entry.photos || []).map(normalisePhoto),
    documents: (entry.documents || []).map(normaliseDocument),
    folderUrl: entry.folderUrl || "",
    source: "script",
  };
}

// ---------------------------------------------------------------------------------------
// What the pages call
// ---------------------------------------------------------------------------------------

/** Always resolves. Returns EMPTY_MEDIA rather than throwing when nothing is configured. */
export async function fetchCollegeMedia(slug) {
  if (!COLLEGE_SLUGS.includes(slug)) return EMPTY_MEDIA;

  if (hasApi()) {
    try {
      return await fromBackend(slug);
    } catch {
      /* fall through to the Apps Script, then to the built-in photographs */
    }
  }
  if (SCRIPT_URL) {
    try {
      return fromScriptPayload(await loadScriptPayload(), slug);
    } catch {
      /* fall through */
    }
  }
  return EMPTY_MEDIA;
}

/**
 * Every college at once, for the main gallery. Against the Apps Script this is a single
 * request; against the backend it is one per college, which is why they run in parallel.
 */
export async function fetchAllMedia() {
  const blank = {};
  COLLEGE_SLUGS.forEach((slug) => {
    blank[slug] = EMPTY_MEDIA;
  });
  if (!hasDriveMedia()) return blank;

  if (!hasApi() && SCRIPT_URL) {
    try {
      const data = await loadScriptPayload();
      const out = {};
      COLLEGE_SLUGS.forEach((slug) => {
        out[slug] = fromScriptPayload(data, slug);
      });
      return out;
    } catch {
      return blank;
    }
  }

  const results = await Promise.all(COLLEGE_SLUGS.map((slug) => fetchCollegeMedia(slug)));
  const out = {};
  COLLEGE_SLUGS.forEach((slug, i) => {
    out[slug] = results[i];
  });
  return out;
}
